import { BoosterType } from '../../Card/BoosterType';
import { IGiftData } from '../GameActions';
import { IEntryPointData, IMessagePayload } from '../IEntryPointData';
import { IGameState } from '../IGameState';
import { InventoryStateHelper } from '../../Player/Inventory/InventoryStateHelper';
import { PlayerStateHelper } from '../../Player/PlayerStateHelper';
import { PlayerTask } from '../../Player/PlayerTasks';
import { IPlayerLevelData, LevelType } from '../../Player/Progress/PlayerLevels';
import { SocialStateHelper } from '../../Player/Social/SocialStateHelper';
import { PopupID } from '../../UI/PopupID';
import { track, TrackMethod } from 'Utility/Tracking/Track';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import Game from 'Game';
import { TActionType, TransactionType, TSocialActionType } from 'Utility/Tracking/DataSet/Transaction';
import { IBotMessagePayload } from 'Utility/EntryPoint/Payloads/IBotMessagePayload';
import { PayloadType } from 'Utility/SDK/PayloadType';
import { trackReward } from 'Utility/Tracking/TrackingHelpers/Resource';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { currencyShareReward, inviteShareReward } from 'Constants/Economy';
import { EntryOrigin } from 'Utility/Tracking/DataSet/EntryPoint';
import { report } from 'report';
import { FBPixel } from 'Utility/SDK/FBPixelHandler';
import { isCrossPromoEntryPoint, isCrossPromoPayload, isCustomEntryPayload } from 'Utility/EntryPoint/EntryPointHelper';
import { ICrossPromoPayload } from 'Utility/EntryPoint/Payloads/ICrossPromoPayload';
import { CustomPayloadPlacement, ICustomPayload } from 'Utility/EntryPoint/Payloads/ICustomPayload';
import { EntryPointType } from 'Utility/EntryPoint/EntryPointType';

export default class PayloadStateHelper {
    public static handleEntryPoint(state: IGameState, entryPoint: IEntryPointData): IGameState {
        if (!entryPoint) return state;

        try {
            if (entryPoint.entryPoint === 'A2U' && entryPoint.payload) {
                state = this.handleA2UPayload(state, entryPoint.payload);
            }
            // Handle xpromo entrypoint
            if (isCrossPromoEntryPoint(entryPoint)) {
                state = this.handleXPromoPayload(state, entryPoint.payload);
                // Handle xpromo payload
            } else if (isCrossPromoPayload(entryPoint)) {
                state = this.handleXPromoPayload(state, entryPoint);
            }

            // Handle bot payload
            if (entryPoint && entryPoint.entryPoint) {
                state = this.handleBotMessagePayload(state, entryPoint);
            }

            // Handle internal payloads
            if (entryPoint.payload && entryPoint.payload.data) {
                state = this.handleInternalPayload(state, entryPoint.payload as ICustomPayload);
            } else if (isCustomEntryPayload(entryPoint)) {
                state = this.handleInternalPayload(state, entryPoint);
            }

            // Simply check for existence of `fb_instant_game_ad_id` as `entrypoint.async` and `entrypoint.entrypoint` cannot be trusted. Do not
            // believe their lies.
            // Added check for entrypoint === EntryPointType.AD, for ticket SOFA-3460, to include new, custom ad campaign entry points
            if (entryPoint.fb_instant_game_ad_id !== undefined || entryPoint.entryPoint === EntryPointType.AD) {
                state = this.handleMarketingCampaignEntrypoint(state, entryPoint);
            }
        } catch (error) {
            report('Failed to handle entry point', {
                once: true,
                sampleRate: 0.5,
                tags: {
                    placement: entryPoint.payload?.placement,
                    data: entryPoint.payload?.data,
                },
                fingerprint: 'handleEntryPoint:FailedToHandle',
                error,
                state,
            });
        }

        state = {
            ...state,
            entryPoint: {
                ...state.entryPoint,
                handled: true,
            },
        };

        return state;
    }

    private static handleXPromoPayload(state: IGameState, payload: ICrossPromoPayload): IGameState {
        try {
            track('epPromoSource', payload.originGameId);

            state = PayloadStateHelper.appendOtherGameId(state, payload.originGameId, payload.originPlayerId);

            // Prepare `otherGameIds` string for tracking concatenating all persisted game and player IDs per SOFA-3474 spec.
            const otherGameIds = state.playerState.otherGameIds
                .map(other => `${other.gameId}:${other.playerId}`)
                .join(',');
            track('otherGameIds', otherGameIds);

            Game.sdk.gameTracking(FBTrackingEvent.X_PROMO);
            Game.sdk.gameTracking(
                state.playerState.levels[LevelType.Core].length === 0
                    ? FBTrackingEvent.X_PROMO_NEW
                    : FBTrackingEvent.X_PROMO_OLD,
                undefined,
                {
                    reward: payload.promoReward ? 'yes' : 'no',
                    source: payload.originGameId,
                }
            );
        } catch (e) {
            console.warn(e);
        }

        return state;
    }

    private static appendOtherGameId(state: IGameState, gameId: string, playerId: string): IGameState {
        // Only add if game is not already defined.
        if (state.playerState.otherGameIds.some(other => other.gameId === gameId)) return state;

        // Add new game and player ID.
        const playerState = PlayerStateHelper.updatePlayerState(state, {
            ...state.playerState,
            otherGameIds: [...state.playerState.otherGameIds, { gameId, playerId }],
        });

        return {
            ...state,
            playerState,
        };
    }

    private static handleA2UPayload(state: IGameState, payload: IMessagePayload): IGameState {
        // Validate this 'message' has not already been claimed.
        if (SocialStateHelper.hasReceivedMessage(state.playerState, payload.messageId)) {
            console.warn('message seen already.');
            return state;
        }
        let playerState = SocialStateHelper.retireMessage(state.playerState, payload.messageId);
        playerState = InventoryStateHelper.giveReward(playerState, payload.reward);
        trackResourceEvent(state, { transactionType: TransactionType.REWARD, tActionType: TActionType.A2U });

        return PlayerStateHelper.setPlayerState(state, playerState);
    }

    private static handleBotMessagePayload(
        state: IGameState,
        payload: { gamePayload?: IBotMessagePayload; [key: string]: any }
    ): IGameState {
        if (
            payload.gamePayload &&
            payload.gamePayload.gift &&
            !SocialStateHelper.hasReceivedMessage(state.playerState, payload.messageId)
        ) {
            track('tActionType', TActionType.BOT);

            state = PlayerStateHelper.setPlayerState(
                state,
                SocialStateHelper.retireMessage(state.playerState, payload.messageId)
            );

            payload.gamePayload.gift.giftData.forEach((giftData: IGiftData) => {
                state = {
                    ...state,
                    playerState: InventoryStateHelper.giveReward(
                        state.playerState,
                        {
                            type: giftData.itemId,
                            value: giftData.amount,
                        },
                        { queueTask: true }
                    ),
                };
            });

            trackResourceEvent(state, { transactionType: TransactionType.REWARD, tActionType: TActionType.BOT });
        }

        return state;
    }

    private static handleInternalPayload(state: IGameState, payload: ICustomPayload): IGameState {
        // Validate this 'message' has not already been claimed.
        if (SocialStateHelper.hasReceivedMessage(state.playerState, payload.messageId)) {
            console.warn('message seen already.');
            return state;
        }

        // Validate there is a userId with the payload and it is not the current user.
        if (!payload.userId || payload.userId === state.userState.userId) {
            return state;
        }

        // Add message ID to 'receivedMessageIDs' array.
        state = PlayerStateHelper.setPlayerState(
            state,
            SocialStateHelper.retireMessage(state.playerState, payload.messageId)
        );

        switch (payload.data) {
            case PayloadType.SEND_CURRENCY:
                Game.sdk.gameTracking(FBTrackingEvent.Coin_Send_Entry);

                // TODO: give reward here instead of in the menu
                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.GIFT_MENU,
                            meta: {
                                reward: payload.reward,
                                payload,
                            },
                        },
                    ]),
                };
                return state;
            case PayloadType.REQUEST_CURRENCY:
                Game.sdk.gameTracking(FBTrackingEvent.Coin_Request_Entry);
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(
                        state.playerState,
                        [
                            {
                                task: PlayerTask.SEND_COINS,
                                reward: currencyShareReward,
                                payload,
                            },
                        ],
                        true
                    ),
                };
            case PayloadType.HELP_FRIEND:
                Game.sdk.gameTracking(FBTrackingEvent.Helper_Entry);
                track('tHelperCards', 1, TrackMethod.ADD);
                state = {
                    ...state,
                    playerState: InventoryStateHelper.addInventory(state.playerState, BoosterType.PLUS_ONE, 1),
                };
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.EXTRA_CARDS,
                            cards: payload.cards,
                            payload,
                        },
                    ]),
                };
            case PayloadType.INVITE_FRIEND:
                switch (payload.placement) {
                    case CustomPayloadPlacement.SOCIAL_HUB:
                        Game.sdk.gameTracking(FBTrackingEvent.SocialHub_Invite_Entry);
                        break;
                    case CustomPayloadPlacement.TOP_LIST:
                        Game.sdk.gameTracking(FBTrackingEvent.TopList_Invite_Entry);
                        break;
                    default:
                        break;
                }

                const levels: IPlayerLevelData[] = state.playerState.levels[LevelType.Core];
                if (levels[0] != null && levels[0].stars != null) return state;

                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SEND_COINS,
                            reward: inviteShareReward,
                            payload,
                        },
                    ]),
                };

                track('socialActionType', TSocialActionType.INVITE);
                trackReward(inviteShareReward);
                track('socialRecieve', 1);
                trackResourceEvent(state, {
                    transactionType: TransactionType.REWARD,
                    tActionType: TActionType.INVITE,
                });
                return state;
            case PayloadType.SEND_FRIEND_GIFT:
                switch (payload.placement) {
                    case CustomPayloadPlacement.SAGA_MAP:
                        Game.sdk.gameTracking(FBTrackingEvent.SagaMap_Coins_Entry);
                        break;
                    case CustomPayloadPlacement.TOP_LIST:
                        Game.sdk.gameTracking(FBTrackingEvent.Top_List_Coins_Entry);
                        break;
                    default:
                        break;
                }
                return state;
            default:
                return state;
        }
    }

    private static handleMarketingCampaignEntrypoint(state: IGameState, entryPoint: IEntryPointData): IGameState {
        const {
            mAdId,
            mAdsetID,
            mCampaignID,
            mAdName,
            mAdType,
            mChannel,
            attributionCampaignID,
        } = PayloadStateHelper.parseMarketingCampaignEntryPoint(entryPoint);

        track('mAdId', mAdId);
        track('mAdsetID', mAdsetID);
        track('mCampaignID', mCampaignID);
        track('mAdName', mAdName);
        track('attributionCampaignID', mAdName);
        track('mAdType', mAdType);
        track('mChannel', mChannel);
        track('mOrigin', EntryOrigin.Marketing);

        FBPixel.track('marketing_campaign', {
            mAdId,
            mAdsetID,
            mCampaignID,
            mAdName,
            mAdType,
            mChannel,
        });

        // Only update persisted `attributionCampaignID` value if the campaign ID is provided by `entryPoint.payload?.mAdName` for custom payloads
        // or `entryPoint.fb_instant_game_campaign_id` for campaigns without payloads.
        if (attributionCampaignID === undefined) return state;

        return {
            ...state,
            playerState: {
                ...state.playerState,
                attributionCampaignID,
            },
        };
    }

    /**
     * Parse out important marketing campaign data from an entry point
     */
    private static parseMarketingCampaignEntryPoint(entryPoint: IEntryPointData) {
        const mAdId = entryPoint.fb_instant_game_ad_id ?? entryPoint.payload?.mAdID;
        const mAdsetID = entryPoint.fb_instant_game_adset_id;
        const mCampaignID = entryPoint.fb_instant_game_campaign_id;

        return {
            mAdId: mAdId ? String(mAdId) : undefined,
            mAdsetID: mAdsetID ? String(mAdsetID) : undefined,
            mCampaignID: mCampaignID ? String(mCampaignID) : undefined,
            mAdName: entryPoint.payload?.mAdName,
            mAdType: entryPoint.payload?.mAdType,
            mChannel: entryPoint.payload?.mChannel,
            // Update `attributionCampaignID`. This may change if a user re-engages with SOFA via a new campaign.
            attributionCampaignID: entryPoint.payload?.mAdName ?? entryPoint.fb_instant_game_campaign_id,
        };
    }
}
